import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Cookie } from "@phosphor-icons/react";

/**
 * UK PECR + EU GDPR cookie disclosure banner.
 *
 * JobVault only uses strictly-necessary cookies (auth + CSRF) — no analytics,
 * no advertising, no third-party trackers. PECR exempts strictly-necessary cookies
 * from prior consent, but we must still inform users. So this is a one-time notice,
 * not a consent gate. Dismissal is stored in localStorage.
 *
 * If we ever add non-essential cookies (analytics, etc.) this banner must be
 * upgraded to a proper opt-in / opt-out consent gate with category toggles.
 */
const STORAGE_KEY = "jv_cookie_notice_dismissed_v1";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) setVisible(true);
    } catch { /* private-browsing fallback: just show once */ setVisible(true); }
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, new Date().toISOString()); } catch { /* noop */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:max-w-md z-50"
      role="region"
      aria-label="Cookie notice"
    >
      <div className="card-hard rounded-md bg-white p-4 shadow-[6px_6px_0_var(--brand-ink)]">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-9 h-9 rounded-md bg-[var(--brand-bg)] flex items-center justify-center border-[1.5px] border-[var(--brand-ink)]">
            <Cookie size={18} weight="duotone"/>
          </div>
          <div className="flex-1 text-sm leading-relaxed">
            <p className="font-medium mb-1">we use one cookie. that&apos;s it.</p>
            <p className="text-[var(--brand-muted)]">
              a strictly-necessary session cookie keeps you logged in. no analytics, no ad pixels, no third-party trackers.{" "}
              <Link to="/cookies" className="underline font-medium text-[var(--brand-ink)]" data-testid="cookie-banner-policy">
                cookie policy →
              </Link>
            </p>
          </div>
          <button
            onClick={dismiss}
            data-testid="cookie-banner-dismiss"
            aria-label="dismiss cookie notice"
            className="shrink-0 -mt-1 -mr-1 w-8 h-8 rounded hover:bg-[var(--brand-bg)] flex items-center justify-center"
          >
            <X size={16} weight="bold"/>
          </button>
        </div>
        <button
          onClick={dismiss}
          data-testid="cookie-banner-accept"
          className="mt-3 w-full btn-hard bg-[var(--brand-ink)] text-white rounded-md px-3 py-2 text-sm font-medium"
        >
          got it
        </button>
      </div>
    </div>
  );
}
